import { IPaginaInfo, PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import ConciliacaoAuttarTableBasic, { IConciliacaoAuttarTableBasicProps } from "./ConciliacaoAuttarTableBasic";
import { Col, Divider, Empty, Form, FormProps, Button, Row } from "antd";
import { useConciliacaoAuttarPaginar } from "@/Services/Financeiro/Conta/ConciliacaoAuttarService";
import { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import Trabalho from "@/Models/Sistema/Trabalho";
import ComponentePermissao from "@/Components/Base/Permissao/ComponentePermissao";
import ConciliacaoAuttarFormModal from "./ConciliacaoAuttarFormModal";
import { EnumTrabalhoTipo } from "@/Models/Sistema/Trabalho/TrabalhoTipo";

type Filtro = {
    pagina: IPaginaInfo;
    trabalho?: Trabalho;
    ativo?: boolean;
    intervalo?: [Dayjs, Dayjs];
};

type FormOverload = Omit<FormProps<Filtro>, "initialValues"> & Partial<{ initialValues: Filtro }>;

export interface IConciliacaoAuttarConsultaScreenProps extends FormOverload {
    tableProps?: Omit<IConciliacaoAuttarTableBasicProps, "dataSource">;
    codTrabalhoTipo:EnumTrabalhoTipo;
}

export default function ConciliacaoAuttarConsultaScreen({
    form: formParam,
    initialValues,
    tableProps,
    component,
    layout,
    codTrabalhoTipo,
    ...rest
}: IConciliacaoAuttarConsultaScreenProps) {
    const [_formInterno] = Form.useForm<Filtro>();
    const form = formParam ?? _formInterno;

    const [filtro, setFiltro] = useState<Filtro>(initialValues ?? { pagina: new PaginaInfo(1, 15) });

    const { data, isLoading, isPending, refetch, isSuccess, isError } = useConciliacaoAuttarPaginar({
        pagina: filtro.pagina ?? new PaginaInfo(),
        dataInical: filtro.intervalo?.[0]?.toDate(),
        dataFinal: filtro.intervalo?.[1]?.toDate(),
        ativo : true,
        enabled: false,
        codTrabalhoTipo
    });

    useEffect(() => {
        refetch();
    }, [filtro]);

    return (
        <div>
            <Form
                {...rest}
                form={form}
                component={component === undefined ? "div" : component}
                layout={layout === undefined ? "vertical" : layout}
                initialValues={{
                    ...initialValues,
                    pagina: initialValues?.pagina === undefined ? new PaginaInfo() : initialValues?.pagina,
                    ativo: initialValues?.ativo === undefined ? true : initialValues.ativo,
                }}
                onFinish={(data) => {
                    setFiltro(data);
                }}
            >
                <Row gutter={8} wrap>
                    <ComponentePermissao permissoes={["FinanceiroContaMovimentacaoListarTodos"]}>
                        <Col xs={"auto"}>
                            <Button type="primary" loading={isLoading} onClick={() => refetch()}>
                                Listar
                            </Button>
                        </Col>
                    </ComponentePermissao>
                    <ComponentePermissao permissoes={["FinanceiroContaMovimentacaoInserir"]}>
                        <Col xs={"auto"}>
                            <ConciliacaoAuttarFormModal
                                codTrabalhoTipo={codTrabalhoTipo}
                                onSuccess={() => refetch()}
                                textButtonOpen="Importar"
                                title="Importar"
                                propsButtonOpen={{ size: "middle", type: "dashed" }}
                            />
                        </Col>
                    </ComponentePermissao>
                </Row>

                <Form.Item hidden name="ativo"></Form.Item>
            </Form>

            {((isSuccess || isLoading) && (
                <>
                    <Divider>Registros</Divider>
                    <ComponentePermissao permissoes={["FinanceiroContaConciliacaoAuttarListar"]}>
                        <ConciliacaoAuttarTableBasic
                            {...tableProps}
                            codTrabalhoTipo={codTrabalhoTipo}
                            dataSource={data}
                            onSuccess={() => refetch()}
                            loading={isLoading || isPending ? true : tableProps?.loading}
                            onChange={(page) => {
                                setFiltro((prev) => ({
                                    ...prev,
                                    pagina: new PaginaInfo(page.current, page.pageSize),
                                }));
                            }}
                        />
                    </ComponentePermissao>
                </>
            )) || (
                <>
                    <Empty description={isError ? "Ocorreu um erro, tente novamente!" : 'Clique em "Listar" para realizar a consulta.'} />
                </>
            )}
        </div>
    );
}
